import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import bunkerPortService from '../services/bunkerPortService';
import cargoPortService from '../services/cargoPortService';
import COUNTRIES_ISO from '../data/countriesIso';
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';
// Reuse the System Data table / modal / form styling. Those classes
// (.employees-table, .admin-modal-overlay, .modal, .form-input, .btn-*, …)
// are defined globally there; the PortsManager-scoped overrides live in
// PortsManager.css.
import './SystemDataManagement.css';
import './PortsManager.css';

const PAGE_SIZE = 50;

/**
 * Surface the SPECIFIC backend reason for a failed write.
 *
 * The bunker/admin port endpoints return ``{error: '<category>', message:
 * '<specific reason>'}``. The shared ``getErrorMessage`` prefers ``data.error``,
 * so it would only ever show the generic "Validation error" and swallow the
 * actionable detail (e.g. "… is already on the bunker list."). Here we surface
 * ``data.message`` instead, still running it through ``getErrorMessage``'s
 * technical-pattern scrub so nothing unsafe leaks. Scoped to this component on
 * purpose — flipping the global precedence would touch ~50 unrelated call sites.
 */
const extractApiError = (err, fallback) => {
  const data = err?.response?.data;
  const specific = (data && typeof data.message === 'string' && data.message) ? data.message : null;
  return getErrorMessage(specific ? { response: { data: { error: specific } } } : err, fallback);
};

/**
 * MasterPortPicker — searchable typeahead over the FULL UN/LOCODE master port
 * table (bunker AND non-bunker rows), so a user can flag an existing port as
 * bunker-eligible instead of re-keying it (which collides on the unique
 * unlocode). Server-side search, debounced; ports already on the bunker list
 * are shown but disabled.
 */
const MasterPortPicker = ({ selected, onSelect, onClear, mod }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const q = search.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { ports } = await mod.service.searchMasterPorts({ search: q, limit: 25 });
        setResults(ports);
      } catch (e) {
        console.error('Master port search failed:', e);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const displayValue = selected
    ? `${selected.port_name} (${selected.unlocode}) — ${selected.country_name}`
    : '';

  const handlePick = (port) => {
    if (port[mod.flagKey]) return; // already on the list — not selectable
    onSelect(port);
    setSearch('');
    setOpen(false);
  };

  const q = search.trim();

  return (
    <div className="bpm-master-picker" ref={wrapperRef}>
      <div className="bpm-master-picker-input-row">
        <FontAwesomeIcon icon={faSearch} className="bpm-master-picker-icon" />
        <input
          type="text"
          className="form-input bpm-master-picker-input"
          value={open ? search : displayValue}
          placeholder="Search by port name, UN/LOCODE, or country…"
          onFocus={() => { setOpen(true); setSearch(''); }}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
        />
        {selected && !open && (
          <button
            type="button"
            className="bpm-master-picker-clear"
            onClick={onClear}
            title="Clear selection"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
      {open && (
        <div className="bpm-master-picker-dropdown" role="listbox">
          {loading ? (
            <div className="bpm-master-picker-empty">Searching…</div>
          ) : q.length < 2 ? (
            <div className="bpm-master-picker-empty">Type at least 2 characters to search the global port list.</div>
          ) : results.length === 0 ? (
            <div className="bpm-master-picker-empty">No matching ports found.</div>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={false}
                className={`bpm-master-picker-option${p[mod.flagKey] ? ' is-disabled' : ''}`}
                disabled={p[mod.flagKey]}
                onClick={() => handlePick(p)}
                title={p[mod.flagKey] ? `Already on the ${mod.listLabel}` : ''}
              >
                <span className="bpm-mp-name">{p.port_name}</span>
                <span className="bpm-mp-meta">({p.unlocode}) — {p.country_name}</span>
                {p[mod.flagKey] && <span className="bpm-mp-badge">On {mod.label} list</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * CountrySelect — a Country dropdown that emits BOTH the country name and its
 * ISO-3166 alpha-2 code, so the ISO field auto-fills (no more "KR26" typos). If
 * the current value isn't in the ISO list (e.g. a legacy/seeded name like
 * "KOREA, REPUBLIC OF"), it's preserved as a selectable "current" option rather
 * than silently dropped.
 */
const CountrySelect = ({ countryName, isoCode, onChange, id }) => {
  const name = countryName || '';
  const match = name
    ? COUNTRIES_ISO.find((c) => c.name.toLowerCase() === name.toLowerCase())
    : null;
  const isLegacy = !!name && !match;
  const currentValue = match ? match.name : name;

  return (
    <select
      id={id}
      className="form-input"
      value={currentValue}
      onChange={(e) => {
        const picked = e.target.value;
        const found = COUNTRIES_ISO.find((c) => c.name === picked);
        if (found) onChange(found.name, found.code);
        else onChange(picked, picked ? (isoCode || '') : '');
      }}
    >
      <option value="">— Select country —</option>
      {isLegacy && (
        <option value={name}>
          {name}{isoCode ? ` (${isoCode})` : ''} — current
        </option>
      )}
      {COUNTRIES_ISO.map((c) => (
        <option key={c.code} value={c.name}>
          {c.name} ({c.code})
        </option>
      ))}
    </select>
  );
};

/**
 * Module registry — one entry per trade module that curates a port list. The
 * panel below is identical for every module, so a new list is an entry here
 * plus a route/tab, not another copy of this file.
 */
export const PORT_MODULES = {
  bunker: {
    key: 'bunker',
    label: 'bunker',
    noun: 'Bunker Port',
    listLabel: 'bunker list',
    flagKey: 'is_bunker_port',
    service: bunkerPortService,
    // What a trader loses when a port comes off this list.
    removalNote: 'Removing this port takes it off the Fuels enquiry port picker.',
  },
  cargo: {
    key: 'cargo',
    label: 'cargo',
    noun: 'Cargo Port',
    listLabel: 'cargo list',
    flagKey: 'is_cargo_port',
    service: cargoPortService,
    removalNote: 'Removing this port takes it off the Cargo port pickers (recaps, freight, offers).',
  },
};

/**
 * PortsManager — self-contained panel for ONE module's curated port list,
 * rendered as a sub-tab of that module's Trade Operations dashboard.
 *
 * A module's ports are just rows in the global UN/LOCODE master
 * ``HMSShippingPort`` table carrying that module's flag. Because that table is
 * pre-seeded with every port, "adding" a port is usually flagging an EXISTING
 * row, not creating one — so the Add modal leads with a search over the master
 * list (``MasterPortPicker``) and keeps a "create new" tab for genuinely-missing
 * ports. The backend forces the module flag on this surface.
 *
 * Removing takes the port OFF this module's list; the shared master row (which
 * the other module and historical deals may use) is never deleted here.
 *
 * The whole panel is wrapped in ``.ports-manager`` so the unscoped ``form {}``
 * rule leaking from Login.css can't disturb the modal layout.
 */
const PortsManager = ({ module = 'bunker' }) => {
  const mod = PORT_MODULES[module] || PORT_MODULES.bunker;
  const { showSuccess, showError } = useFlashMessage();
  const [ports, setPorts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Server-side search
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dropdown state (matching employee/system-data tables)
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit' | 'delete'
  const [createTab, setCreateTab] = useState('existing'); // 'existing' | 'new'
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchPorts = async ({ append = false, offset = 0 } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const { ports: page, total: count } = await mod.service.getPorts({
        search: searchTerm,
        limit: PAGE_SIZE,
        offset,
      });
      setTotal(count);
      setPorts(prev => (append ? [...prev, ...page] : page));
    } catch (err) {
      console.error(`Error fetching ${mod.label} ports:`, err);
      setError(getErrorMessage(err, `${mod.noun}s could not be loaded. Please try again.`));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Reload from the top whenever the committed search term changes.
  useEffect(() => {
    fetchPorts({ append: false, offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Click outside handler to close dropdowns (matching SystemDataManagement).
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeDropdown) {
        setActiveDropdown(null);
        // Remove dropdown-open class from all dropdowns
        document.querySelectorAll('.action-dropdown').forEach(dropdown => {
          dropdown.classList.remove('dropdown-open');
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const handleLoadMore = () => {
    if (loadingMore) return;
    fetchPorts({ append: true, offset: ports.length });
  };

  const toggleDropdown = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);

    // Add/remove dropdown-open class to the action-dropdown element so the
    // orange caret rotates and the active gear button is elevated/darkened
    // (driven by the .action-dropdown.dropdown-open rules in
    // SystemDataManagement.css).
    setTimeout(() => {
      document.querySelectorAll('.action-dropdown').forEach(dropdown => {
        dropdown.classList.remove('dropdown-open');
      });

      if (activeDropdown !== dropdownId) {
        const element = document.querySelector(`[data-dropdown-id="${dropdownId}"]`);
        if (element) {
          element.classList.add('dropdown-open');
        }
      }
    }, 0);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Country dropdown emits name + ISO together; ISO is derived, never typed.
  const handleCountryChange = (name, code) => {
    setFormData(prev => ({ ...prev, country_name: name, country_iso_code: code }));
  };

  const handleCreate = () => {
    setModalMode('create');
    setCreateTab('existing');
    setSelectedExisting(null);
    setSelectedPort(null);
    setFormData({});
    setError(null);
    setShowModal(true);
    document.body.classList.add('modal-open');
  };

  const handleEdit = (port) => {
    setActiveDropdown(null);
    setModalMode('edit');
    setSelectedPort(port);
    setFormData({ ...port });
    setError(null);
    setShowModal(true);
    document.body.classList.add('modal-open');
  };

  const handleDeleteClick = (port) => {
    setActiveDropdown(null);
    setModalMode('delete');
    setSelectedPort(port);
    setShowModal(true);
    document.body.classList.add('modal-open');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPort(null);
    setSelectedExisting(null);
    setFormData({});
    setError(null);
    document.body.classList.remove('modal-open');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (modalMode === 'create') {
        if (createTab === 'existing') {
          if (!selectedExisting) return;
          const res = await mod.service.addExistingPort(selectedExisting.id);
          showSuccess(res?.message || `Port added to the ${mod.listLabel}`);
        } else {
          const res = await mod.service.createPort({
            unlocode: formData.unlocode,
            port_name: formData.port_name,
            country_name: formData.country_name,
            country_iso_code: formData.country_iso_code,
            latitude: formData.latitude ?? null,
            longitude: formData.longitude ?? null,
          });
          // The backend may have promoted an existing master row instead of
          // creating a new one — surface its message either way.
          showSuccess(res?.message || `${mod.noun} created successfully`);
        }
      } else if (modalMode === 'edit') {
        // ``unlocode`` is immutable post-create — never send it on a PATCH.
        // eslint-disable-next-line no-unused-vars
        const {
          unlocode: _ignoredUnlocode,
          id: _ignoredId,
          is_bunker_port: _ignoredBunkerFlag,
          is_cargo_port: _ignoredCargoFlag,
          ...patch
        } = formData;
        await mod.service.updatePort(selectedPort.id, patch);
        showSuccess(`${mod.noun} updated successfully`);
      } else if (modalMode === 'delete') {
        const res = await mod.service.removePort(selectedPort.id);
        showSuccess(res?.message || `Port removed from the ${mod.listLabel}`);
      }

      setShowModal(false);
      setSelectedPort(null);
      setSelectedExisting(null);
      setFormData({});
      document.body.classList.remove('modal-open');
      await fetchPorts({ append: false, offset: 0 });
    } catch (err) {
      console.error(`Error submitting ${mod.label} port:`, err);
      const errorMessage = extractApiError(err, `${mod.noun} could not be saved. Please try again.`);
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Validation for the active create/edit form.
  const createDisabled = submitting || (() => {
    if (modalMode === 'delete') return false;
    if (modalMode === 'create' && createTab === 'existing') {
      return !selectedExisting || !!selectedExisting[mod.flagKey];
    }
    // create 'new' tab + edit share the same required fields.
    const base = !formData.port_name || !formData.country_name || !formData.country_iso_code;
    if (modalMode === 'create') return base || !formData.unlocode;
    return base;
  })();

  // Shared port-detail fields used by the "create new" tab and the edit form.
  const renderPortFields = (mode) => (
    <>
      <div className="form-group">
        <label htmlFor="port-field-unlocode">UN/LOCODE <span className="required">*</span></label>
        <input
          type="text"
          id="port-field-unlocode"
          className="form-input"
          value={formData.unlocode || ''}
          onChange={(e) => handleInputChange('unlocode', e.target.value.toUpperCase())}
          placeholder="e.g. SGSIN"
          maxLength={10}
          required
          readOnly={mode === 'edit'}
          style={mode === 'edit' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
        />
        {mode === 'edit' && (
          <small style={{ color: '#666', fontSize: '12px' }}>
            UN/LOCODE cannot be changed after creation. Delete and recreate to change it.
          </small>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="port-field-name">Port Name <span className="required">*</span></label>
        <input
          type="text"
          id="port-field-name"
          className="form-input"
          value={formData.port_name || ''}
          onChange={(e) => handleInputChange('port_name', e.target.value)}
          placeholder="e.g. Port of Singapore"
          maxLength={100}
          required
        />
      </div>

      <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <div>
          <label htmlFor="port-field-country">Country Name <span className="required">*</span></label>
          <CountrySelect
            id="port-field-country"
            countryName={formData.country_name}
            isoCode={formData.country_iso_code}
            onChange={handleCountryChange}
          />
        </div>
        <div>
          <label htmlFor="port-field-iso">ISO Code <span className="required">*</span></label>
          <input
            type="text"
            id="port-field-iso"
            className="form-input"
            value={formData.country_iso_code || ''}
            placeholder="SG"
            maxLength={10}
            readOnly
            title="Set automatically from the selected country"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>
      </div>

      <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label htmlFor="port-field-lat">Latitude</label>
          <input
            type="number"
            id="port-field-lat"
            className="form-input"
            value={formData.latitude ?? ''}
            onChange={(e) => handleInputChange('latitude', e.target.value === '' ? null : Number(e.target.value))}
            placeholder="-90 to 90"
            step="any"
            min={-90}
            max={90}
          />
        </div>
        <div>
          <label htmlFor="port-field-lng">Longitude</label>
          <input
            type="number"
            id="port-field-lng"
            className="form-input"
            value={formData.longitude ?? ''}
            onChange={(e) => handleInputChange('longitude', e.target.value === '' ? null : Number(e.target.value))}
            placeholder="-180 to 180"
            step="any"
            min={-180}
            max={180}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="ports-manager">
      <div className="ports-toolbar">
        <form onSubmit={handleSearchSubmit} className="ports-search-form">
          <div className="ports-search-input-wrap">
            <FontAwesomeIcon icon={faSearch} className="ports-search-icon" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by port, UN/LOCODE or country…"
              className="ports-search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleSearchClear}
                className="ports-search-clear"
                title="Clear search"
              >
                &times;
              </button>
            )}
          </div>
          <button type="submit" className="ports-search-submit">
            Search
          </button>
        </form>
        <div className="ports-toolbar-actions">
          <button
            className="create-btn"
            onClick={handleCreate}
            title={`Add ${mod.noun}`}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      {error && !showModal && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading {mod.label} ports...</div>
      ) : (
        <table className="employees-table">
          <thead>
            <tr>
              <th>UN/LOCODE</th>
              <th>Port Name</th>
              <th>Country</th>
              <th>ISO</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ports.length === 0 ? (
              <tr>
                <td colSpan={5} className="no-data">
                  <i className="fas fa-inbox"></i>
                  <p>No {mod.label} ports found. Add one to get started.</p>
                </td>
              </tr>
            ) : (
              ports.map(port => (
                <tr key={port.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{port.unlocode}</td>
                  <td>{port.port_name}</td>
                  <td>{port.country_name}</td>
                  <td style={{ fontFamily: 'monospace', color: '#6b7280' }}>{port.country_iso_code}</td>
                  <td className="actions-cell">
                    <div
                      className="action-dropdown"
                      onClick={(e) => e.stopPropagation()}
                      data-dropdown-id={`${mod.key}-port-${port.id}`}
                    >
                      <button
                        className="gear-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(`${mod.key}-port-${port.id}`);
                        }}
                      >
                        Actions
                      </button>
                      {activeDropdown === `${mod.key}-port-${port.id}` && (
                        <div className="dropdown-menu">
                          <button onClick={() => handleEdit(port)}>
                            Edit
                          </button>
                          <hr />
                          <button className="danger" onClick={() => handleDeleteClick(port)}>
                            Remove from list
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {!loading && ports.length > 0 && (
        <div className="ports-show-more-row">
          <span className="ports-show-more-count">
            Showing <strong>{ports.length}</strong> of <strong>{total}</strong>{' '}
            {total === 1 ? 'port' : 'ports'}
            {searchTerm ? ' (filtered)' : ''}
          </span>
          {ports.length < total && (
            <button
              type="button"
              className="ports-show-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore
                ? 'Loading…'
                : `Show ${Math.min(PAGE_SIZE, total - ports.length)} more`}
            </button>
          )}
        </div>
      )}

      {/* Create / Edit / Delete modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'create' && `Add ${mod.noun}`}
                {modalMode === 'edit' && `Edit ${mod.noun}`}
                {modalMode === 'delete' && `Remove from ${mod.listLabel}`}
              </h2>
              <button className="close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              {modalMode === 'delete' ? (
                <div className="delete-confirmation">
                  <i className="fas fa-exclamation-triangle warning-icon"></i>
                  <p>
                    Remove{' '}
                    <strong>{selectedPort?.port_name} ({selectedPort?.unlocode})</strong>{' '}
                    from the {mod.listLabel}?
                  </p>
                  <div className="warning-box">
                    <i className="fas fa-info-circle"></i>
                    <p>
                      {mod.removalNote} The port itself stays in the global port
                      list, and existing deals keep their stored port reference.
                    </p>
                  </div>
                </div>
              ) : modalMode === 'create' ? (
                <>
                  <div className="bpm-tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={createTab === 'existing'}
                      className={`bpm-tab${createTab === 'existing' ? ' is-active' : ''}`}
                      onClick={() => setCreateTab('existing')}
                    >
                      Add existing port
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={createTab === 'new'}
                      className={`bpm-tab${createTab === 'new' ? ' is-active' : ''}`}
                      onClick={() => setCreateTab('new')}
                    >
                      Create new port
                    </button>
                  </div>

                  {createTab === 'existing' ? (
                    <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
                      <p className="bpm-hint">
                        Search the global port list and add it to your {mod.label} list.
                        Country, ISO and coordinates come from the master record.
                      </p>
                      <div className="form-group">
                        <label>Find a port <span className="required">*</span></label>
                        <MasterPortPicker
                          selected={selectedExisting}
                          onSelect={setSelectedExisting}
                          onClear={() => setSelectedExisting(null)}
                          mod={mod}
                        />
                      </div>

                      {selectedExisting && (
                        <div className="bpm-selected-preview">
                          <div className="bpm-preview-row">
                            <span className="bpm-preview-label">UN/LOCODE</span>
                            <span className="bpm-preview-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {selectedExisting.unlocode}
                            </span>
                          </div>
                          <div className="bpm-preview-row">
                            <span className="bpm-preview-label">Port</span>
                            <span className="bpm-preview-value">{selectedExisting.port_name}</span>
                          </div>
                          <div className="bpm-preview-row">
                            <span className="bpm-preview-label">Country</span>
                            <span className="bpm-preview-value">
                              {selectedExisting.country_name} ({selectedExisting.country_iso_code})
                            </span>
                          </div>
                          {(selectedExisting.latitude != null || selectedExisting.longitude != null) && (
                            <div className="bpm-preview-row">
                              <span className="bpm-preview-label">Coordinates</span>
                              <span className="bpm-preview-value" style={{ fontFamily: 'monospace' }}>
                                {selectedExisting.latitude ?? '—'}, {selectedExisting.longitude ?? '—'}
                              </span>
                            </div>
                          )}
                          {selectedExisting[mod.flagKey] && (
                            <div className="bpm-preview-note">
                              This port is already on the {mod.listLabel}.
                            </div>
                          )}
                        </div>
                      )}
                    </form>
                  ) : (
                    <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
                      <p className="bpm-hint">
                        Only use this for a port that isn&apos;t already in the global list.
                        If it already exists, it&apos;ll simply be added to the {mod.label} list.
                      </p>
                      {renderPortFields('create')}
                    </form>
                  )}
                </>
              ) : (
                <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
                  {renderPortFields('edit')}
                </form>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={`btn ${modalMode === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                onClick={handleSubmit}
                disabled={modalMode === 'delete' ? submitting : createDisabled}
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : (
                  <>
                    {modalMode === 'create' && (createTab === 'existing' ? `Add to ${mod.listLabel}` : 'Create')}
                    {modalMode === 'edit' && 'Save Changes'}
                    {modalMode === 'delete' && 'Remove'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortsManager;
